import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { persistentKeyboardPreserveFocus } from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import {
  captureDailyReviewMilestone,
  completeDailyReviewMilestone,
} from "@-/dev.improve.haniverse.lib.cards.review";
import { watchAsyncStep } from "@-/dev.improve.haniverse.lib.async-watchdog";
import {
  createLearnCardOperationId,
  executeLearnCardAction,
  type LearnCardAction,
  type LearnCardActionReceipt,
  type LearnCardActionSnapshot,
  type LearnCardActionTrigger,
  learnCardReceiptAnalyticsEvents,
  undoLearnCardActionReceipt,
} from "@-/dev.improve.haniverse.lib.learn.card-action";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import type { LearnCardDestinationOrigin } from "@-/dev.improve.haniverse.lib.learn.destination";
import { findGraphemeLessonLevel, type Level } from "@-/dev.improve.haniverse.lib.level";
import { useLevels } from "@-/dev.improve.haniverse.routes.nav.app._lib.level";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { useCreateNewMorphemeLessonUserCard } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.new-morpheme-user-card";
import { useCallback, useMemo } from "react";

/**
 * Persists a frozen card action and its receipt effects. The deck and its history own destination
 * resolution and navigation, so this boundary does not construct the full submit pipeline's
 * resolver, navigator families, or submit mutation. Query keys and write-clock reads stay shared
 * with the route-driven controller. The one route enrollment can offer (Settings → Daily New
 * Cards, from the own-limit notice) is passed in by the caller that owns navigation.
 */
export function useLearnCardActionPersistence({
  openDailyNewCardsSettings,
}: {
  /** Opens Settings → Daily New Cards from enrollment's own-limit notice. */
  openDailyNewCardsSettings?: () => void;
} = {}) {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const [pushReviewUndoEntry, dailyReviewCompletedRef] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.pushReviewUndoEntry,
    (c) => c.dailyReviewCompletedRef,
  );
  const enroll = useCreateNewMorphemeLessonUserCard({ openDailyNewCardsSettings });
  const levels: Level[] = useLevels();

  const depsRef = useLatest({
    actorDb,
    dailyReviewCompletedRef,
    enroll,
    levels,
    pushReviewUndoEntry,
  });

  const persist = useCallback(
    async ({
      action,
      isRedo = false,
      operationId = createLearnCardOperationId(),
      snapshot,
      trigger = "unknown",
    }: {
      action: LearnCardAction;
      /** History Forward replaying an undone action (analytics only). */
      isRedo?: boolean;
      /** Reuse the id of a previous attempt to retry it exactly-once. */
      operationId?: string;
      snapshot: LearnCardActionSnapshot;
      /** The input that produced the action (analytics only). */
      trigger?: LearnCardActionTrigger | "unknown";
    }): Promise<LearnCardActionReceipt> => {
      const deps = depsRef.current;
      const now = new Date();
      // A quiz answer is a review too: it counts toward the day's review goal.
      const milestone =
        action.kind === "rate" || action.kind === "answer-quiz"
          ? await captureDailyReviewMilestone(deps.actorDb(import.meta.location), now)
          : null;
      if (action.kind === "rate") {
        // Move first-responder to the module-owned hidden field before the card leaves.
        // Otherwise a touch rating can let UIKit dismiss the keyboard during the transition,
        // even though the next drill is configured to inherit persistent focus.
        await watchAsyncStep("learnCard: preserveKeyboardFocus", () =>
          persistentKeyboardPreserveFocus(),
        );
      }
      // Each step is watched individually: when an action wedges, the ONLY thing that
      // distinguishes the causes is which await never came back.
      const receipt = await watchAsyncStep("learnCard: executeAction", () =>
        executeLearnCardAction({
          action,
          actorDb: deps.actorDb,
          deps: { enroll: deps.enroll },
          now,
          operationId,
          snapshot,
        }),
      );
      // The write has committed. Its follow-up effects are best-effort: rejecting now would tell
      // the deck that nothing landed, and it would offer the card again to be rated twice.
      try {
        applyReceiptEffects(receipt, deps);
        // Keyed by the operation: a retry of the same action reuses its id and records nothing.
        for (const event of learnCardReceiptAnalyticsEvents({
          isRedo,
          receipt,
          snapshot,
          trigger,
        })) {
          captureAnalyticsEvent(event.name, event.properties, { dedupeKey: operationId });
        }
        if (
          (receipt.persisted.kind === "review" ||
            receipt.persisted.kind === "review-batch" ||
            receipt.persisted.kind === "quiz-answer") &&
          (await completeDailyReviewMilestone(deps.actorDb(import.meta.location), milestone))
        ) {
          deps.dailyReviewCompletedRef.current = true;
          captureAnalyticsEvent(
            "daily_review_goal_completed",
            { card_count: milestone?.milestone.cardIds.length ?? 0 },
            { dedupeKey: operationId },
          );
        }
      } catch (error) {
        logger(import.meta.location).error("Learn card: saved action's effects failed", error);
      }
      return receipt;
    },
    [depsRef],
  );

  /** Roll back everything a saved action wrote (see `undoLearnCardActionReceipt`). */
  const undo = useCallback(
    async (receipt: LearnCardActionReceipt) => {
      await watchAsyncStep("learnCard: undoAction", () =>
        undoLearnCardActionReceipt({ actorDb: depsRef.current.actorDb, now: new Date(), receipt }),
      );
      const { persisted } = receipt;
      captureAnalyticsEvent(
        "card_review_undone",
        {
          grapheme:
            "grapheme" in persisted
              ? persisted.grapheme
              : "word" in persisted
                ? persisted.word
                : null,
          kind: persisted.kind,
        },
        { dedupeKey: receipt.operationId },
      );
    },
    [depsRef],
  );

  const originFor = useCallback(
    (snapshot: LearnCardActionSnapshot): LearnCardDestinationOrigin => {
      switch (snapshot.kind) {
        case "review": {
          return { drillType: snapshot.drillType, grapheme: snapshot.grapheme, kind: "review" };
        }
        case "sentence": {
          return { grapheme: snapshot.word, kind: "review" };
        }
        case "lesson": {
          // The grapheme identifies its level in the CURRENT curriculum; the index it was shown
          // at does not survive a mid-session script or HSK-version switch (levels regenerate), so
          // it is only the fallback for a grapheme the current curriculum no longer teaches.
          const levels = depsRef.current.levels;
          const level =
            findGraphemeLessonLevel(levels, snapshot) ??
            levels.find((candidate: Level) => candidate.index === snapshot.levelIndex);
          if (level === undefined) {
            throw new Error(
              `No level teaches ${snapshot.grapheme} (shown at level ${snapshot.levelIndex})`,
            );
          }
          return {
            grapheme: snapshot.grapheme,
            kind: "lesson",
            level,
            ...(snapshot.part === undefined ? {} : { part: snapshot.part }),
            ...(snapshot.pinyin === undefined ? {} : { pinyin: snapshot.pinyin }),
          };
        }
        case "slide": {
          return { kind: "slide", slideSlug: snapshot.slideSlug };
        }
        case "quiz": {
          return { kind: "quiz", quizSlug: snapshot.quizSlug, source: snapshot.source };
        }
      }
    },
    [depsRef],
  );

  return useMemo(() => ({ originFor, persist, undo }), [originFor, persist, undo]);
}

function applyReceiptEffects(
  receipt: LearnCardActionReceipt,
  deps: {
    pushReviewUndoEntry: (entry: {
      drillType: Extract<LearnCardActionReceipt["persisted"], { kind: "review" }>["drillType"];
      fsrsCardId: string;
      grapheme: string;
      rating: Extract<LearnCardActionReceipt["action"], { kind: "rate" }>["rating"];
      userCardReviewId: string;
    }) => void;
  },
): void {
  const { persisted } = receipt;
  switch (persisted.kind) {
    case "review": {
      // Recorded only for a write that LANDED NOW: a retry that found its rows already there
      // must not push a second undo entry for the same review.
      if (!persisted.review.alreadyPersisted && receipt.action.kind === "rate") {
        deps.pushReviewUndoEntry({
          drillType: persisted.drillType,
          fsrsCardId: persisted.review.fsrsCardId,
          grapheme: persisted.grapheme,
          rating: receipt.action.rating,
          userCardReviewId: persisted.review.userCardReviewId,
        });
      }
      return;
    }
    case "review-batch": {
      // A sentence rates several cards at once; the single-card undo stack has no entry shape
      // for that, so — as before — a batch is not undoable.
      return;
    }
    case "quiz-answer": {
      // Undone through the deck history (`undoLearnCardActionReceipt`), which also takes back the
      // enrollment of a first answer; the single-card review undo stack knows only drills.
      return;
    }
    case "enrollment":
    case "marked-known":
    case "skipped":
    case "deferral":
    case "slide-completion":
    case "none": {
      return;
    }
  }
}
